import React, { useMemo, useCallback, useEffect, useState } from 'react'
import PieChart from '../../pie-charts/components/PieChart'
import LineChart from '../../cartesian-charts/components/LineChart'
import BarChart from '../../cartesian-charts/components/BarChart'
import { makeStyles } from 'tss-react/mui'
import { ChartType, ChartView, CustomTrace, FetchingState, LoadingState, RechartData, TraceSuffix } from '../utils/chartModel'
import { BarChartSkeleton, PieChartSkeleton, LineChartSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { getDSTValueFromRange } from '../../../system-data-handler/utils/datetime'
import { ErrorSkeleton } from '@e3dc-react/shell/Elements/Error'
import useResizeObserver from 'use-resize-observer'
import ComposedChart from '../../cartesian-charts/components/ComposedChart'
import ComparisonChart from '../../cartesian-charts/components/ComparisonChart'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { UnitType } from 'src/Libs/types'
import ProgressDetails from './ProgressDetails'
import Summary from './Summary'
import useHideTraces from '../hooks/use-hide-traces'
import { TraceKey, ZoomIndexType } from 'src/Libs/system-charts/shared'
import { HoverPayload } from '../types'
import { AxisInterval } from 'recharts/types/util/types'
import { useFormattedDateTimeForLegend } from '../hooks/use-date-format'
import { useMinPrefix } from '../hooks/use-min-prefix'
import { getRechartDataWithDstSwitchArea } from '../utils/rechart-data-with-dst-switch-area'
import PieChartLegend from '../../pie-charts/components/PieChartLegend'
import { Payload } from 'recharts/types/component/DefaultLegendContent'
import { useHeaderHeight } from '@e3dc-react/shell/Libs/Header'
import { PIE_CHARTS_BREAKPOINT, PIE_CHARTS_BREAKPOINT_XXS } from '../../pie-charts/hooks'
import { CategoricalChartState } from 'recharts/types/chart/types'
import { isNil } from 'lodash'
import { ReferenceArea, ReferenceLine, Tooltip } from 'recharts'
import useIsScrollingY from '../hooks/use-is-scrolling-y'
import getIsTouchDevice from 'src/Libs/utils/get-is-touch-device'
import { useSystemChartsContext } from 'src/Libs/system-charts/shared/providers/SystemChartsProvider'
import { usePrevious } from '@e3dc-react/shell/Hooks'
import { getCartesianGridHeight, getCircleCoordinates, getLeftYAxisMaxValue } from '../utils/indicator-line'
import { usePerformanceAndConsumptionChartsContext } from 'src/Libs/system-charts/default-charts/providers/PerformanceAndConsumptionChartsContext'
import type { CostSavingChartTraceKey } from 'src/Libs/dynamic-power-prices/cost-saving-chart-trace-keys'
import type { PieChartData } from '../../pie-charts/components/pie-chart.types'
import type { ChartEventHandlers } from '../chart-event-handlers'

const SCROLL_Y_AFTER_MOUSE_DOWN_OFFSET = 30

const useStyles = makeStyles()(theme => ({
  chartContainer: {
    width: '100%',
    display: 'flex',
    gap: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pieChartErrorContainer: {
    gridTemplateColumns: '1fr',
  },
  pieChartContainer: {
    display: 'grid',
    alignItems: 'center',
    gap: theme.spacing(2),
    gridTemplateColumns: '1fr',
    gridTemplateRows: '1fr auto',

    [theme.breakpoints.between(PIE_CHARTS_BREAKPOINT_XXS, PIE_CHARTS_BREAKPOINT)]: {
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr',
    },
  },
  pieLegend: {
    alignSelf: 'flex-start',
    justifySelf: 'center',

    [theme.breakpoints.between(PIE_CHARTS_BREAKPOINT_XXS, PIE_CHARTS_BREAKPOINT)]: {
      justifySelf: 'left',
      alignSelf: 'center',
      padding: 0,
    },
  },
}))

export interface ChartProps {
  type: ChartType
  from: Moment
  unit: UnitType
  secondUnit?: string
  minPrefix?: number
  xAxisDomain: number[]
  xAxisFormat: string
  xAxisInterval?: AxisInterval
  rechartData?: RechartData
  customTraces: CustomTrace[] | undefined
  showForecast: boolean
  impossibleTraces: string[]
  traceNames: Record<string, string>
  view: ChartView
  hideLegend?: boolean
  xAxisTicks: number[]
  isUnmodified?: boolean /** is used but no in this file */
  error?: Error
  isInitial?: boolean
  onElementClick?: (props: Record<string, unknown>) => void
  height?: number
  width?: number
  headerHeight?: number
  loadingState?: LoadingState
  traceSuffixes?: TraceSuffix[]
  summaryUnit?: string
  title?: string
  hasBrush?: boolean
  labelTraces?: TraceKey[] | CostSavingChartTraceKey[]
  labelNameKey?: string
  dataQa?: string
  handleZoomMouseDown?: (e: CategoricalChartState) => void
  handleZoomMouseMove?: (e: CategoricalChartState) => void
  handleZoomMouseUp?: () => void
  zoomStartIndex?: ZoomIndexType
  zoomEndIndex?: ZoomIndexType
  zoomWasReset?: boolean
  handleDoubleClick?: () => void
  pieChartData?: PieChartData[]
  pieChartLegendPayload?: Payload[]
  pieChartCells?: React.ReactNode[]
  savingsSum?: number
  setBiggestLegendHeight?: (height: number) => void
  fixedLegendHeight?: number
  isCostSavingsPieChart?: boolean
  isWeightedAveragePriceOn?: boolean
  weightedPrice?: number
  pieChartLabelAction?: React.ReactElement
}

const Chart: React.FC<ChartProps> = ({
  type,
  from,
  unit,
  secondUnit,
  minPrefix = 1,
  xAxisDomain,
  xAxisFormat,
  xAxisInterval,
  rechartData: rechartDataOriginal,
  customTraces,
  showForecast,
  impossibleTraces,
  traceNames,
  view,
  hideLegend,
  xAxisTicks,
  error,
  onElementClick,
  height = 0,
  width = 0,
  headerHeight,
  loadingState,
  traceSuffixes,
  summaryUnit,
  title,
  hasBrush = true,
  labelTraces,
  labelNameKey,
  dataQa,
  handleZoomMouseDown,
  handleZoomMouseMove,
  handleZoomMouseUp,
  zoomStartIndex,
  zoomEndIndex,
  handleDoubleClick,
  zoomWasReset,
  pieChartData,
  pieChartLegendPayload,
  pieChartCells,
  savingsSum,
  setBiggestLegendHeight,
  fixedLegendHeight,
  isCostSavingsPieChart,
  isWeightedAveragePriceOn = false,
  weightedPrice,
  pieChartLabelAction,
}) => {
  const { resetChartSelectionTrigger } = useSystemChartsContext()
  const { presetChartToDisplay } = usePerformanceAndConsumptionChartsContext()
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const [hoverPayload, setHoverPayload] = useState<HoverPayload>()
  const { ref, height: calculatedLegendHeight } = useResizeObserver<HTMLDivElement>({ box: 'border-box' })
  const legendHeight = fixedLegendHeight ?? calculatedLegendHeight
  const { hiddenTraces, onSetHiddenTraces, toggleTraceVisibility } = useHideTraces<string>()
  const isLoading = loadingState?.state === FetchingState.IsFetching
  const dstSwitchTs = view === 'day' || view === 'week' ? getDSTValueFromRange(xAxisDomain[0], xAxisDomain[1]) : undefined
  const [scrollYPosition, setScrollYPosition] = useState(0)
  const isScrollingY = useIsScrollingY()
  const isTouchDevice = getIsTouchDevice()
  const [isWeatherBreakthroughDotVisible, setIsWeatherBreakthroughDotVisible] = useState(true) /** Show or hide the dot on the chart-lines */
  const yAxisComputedMinPrefix = useMinPrefix(rechartDataOriginal, customTraces, minPrefix)

  const hiddenInChart = useMemo(() => {
    if (!customTraces) return []
    return customTraces?.filter(trace => trace.hidden).map(trace => trace.alternateKey ?? trace.key)
  }, [customTraces])

  useEffect(() => {
    if (calculatedLegendHeight !== undefined) {
      setBiggestLegendHeight?.(calculatedLegendHeight)
    }
  }, [calculatedLegendHeight, setBiggestLegendHeight])

  const rechartData = useMemo(() => {
    if (!rechartDataOriginal?.data) return rechartDataOriginal
    const isSummerToWinterSwitch = (dstSwitchTs && moment(rechartDataOriginal.data?.[0]?.ts as number)?.isDST()) || false

    const rechartDataWithDstSwitchArea = getRechartDataWithDstSwitchArea(rechartDataOriginal.data, isSummerToWinterSwitch)

    return { ...rechartDataOriginal, data: rechartDataWithDstSwitchArea } as RechartData
  }, [rechartDataOriginal, dstSwitchTs])

  const successfullyFetchedNoData = rechartData?.loadingState.state === FetchingState.Completed && rechartData?.data?.length === 0

  /* get formatted date-time to display when user hover / not-hover over the chart */
  const { getFormattedDateTimeForLegend } = useFormattedDateTimeForLegend()
  const dateTimeToDisplayAsLegend = getFormattedDateTimeForLegend(view, hoverPayload?.activeLabel, from)

  useEffect(() => {
    const offByDefaultTraces = customTraces?.filter(trace => trace.offByDefault).map(trace => trace.alternateKey ?? trace.key) ?? []

    onSetHiddenTraces(offByDefaultTraces)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTraceToggle = useCallback(
    (traceConfKey: string) => {
      toggleTraceVisibility(traceConfKey)
    },
    [toggleTraceVisibility]
  )

  const getHasScrolledYAfterMouseDown = useCallback(
    (currentScrollY: number): boolean => {
      return isTouchDevice && Math.abs(scrollYPosition - currentScrollY) > SCROLL_Y_AFTER_MOUSE_DOWN_OFFSET
    },
    [isTouchDevice, scrollYPosition]
  )

  const onMouseDown = useCallback(
    (payload: HoverPayload): void => {
      setScrollYPosition(window.scrollY)
      handleZoomMouseDown?.(payload)
    },
    [handleZoomMouseDown]
  )

  const onMouseMove = useCallback(
    (payload: HoverPayload): void => {
      if (!isTouchDevice || (isTouchDevice && !isNil(zoomEndIndex))) setHoverPayload(payload)
      if (!isScrollingY) handleZoomMouseMove?.(payload)
    },
    [isTouchDevice, handleZoomMouseMove, zoomEndIndex, isScrollingY]
  )

  const onMouseUp = useCallback(
    (payload: HoverPayload): void => {
      const hasScrolledYAfterMouseDown = getHasScrolledYAfterMouseDown(window.scrollY)

      if (isTouchDevice && isNil(zoomEndIndex) && hasScrolledYAfterMouseDown) return
      setHoverPayload(payload)
      handleZoomMouseUp?.()
    },
    [handleZoomMouseUp, getHasScrolledYAfterMouseDown, zoomEndIndex, isTouchDevice]
  )

  const onMouseLeave = useCallback((): void => {
    if (upMd) setHoverPayload(undefined)
  }, [upMd])

  const chartEventHandlers: ChartEventHandlers = useMemo(
    () => ({
      onClick: (_, e) => e.stopPropagation(),
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onMouseLeave,
    }),
    [onMouseDown, onMouseMove, onMouseUp, onMouseLeave]
  )

  const previousResetChartSelectionTrigger = usePrevious(resetChartSelectionTrigger)
  const previousFrom = usePrevious(from.toISOString())
  const previousView = usePrevious(view)

  useEffect(() => {
    if (
      zoomWasReset ||
      previousResetChartSelectionTrigger !== resetChartSelectionTrigger ||
      from.toISOString() !== previousFrom ||
      view !== previousView
    ) {
      setHoverPayload(undefined)
    }
  }, [zoomWasReset, previousResetChartSelectionTrigger, resetChartSelectionTrigger, from, previousFrom, view, previousView])

  const skeletonErrorHeight = useMemo(() => {
    if (type === 'pie') {
      return Math.min(width, height)
    }

    return height
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legendHeight, useHeaderHeight, width]) // do not add height to the dependency array, otherwise it will cause an infinite loop

  const chart = useMemo(() => {
    if (error) {
      return <ErrorSkeleton type={`${type}Chart`} title={error.message} />
    }

    if (successfullyFetchedNoData) {
      return <ErrorSkeleton type={`${type}Chart`} title={t('common.noData')} height={skeletonErrorHeight} />
    }

    if (successfullyFetchedNoData && type === 'pie') {
      return (
        <div className={classes.pieChartErrorContainer}>
          <ErrorSkeleton type={`${type}Chart`} title={t('common.noData')} />
        </div>
      )
    }

    const filteredPayload = hoverPayload?.activePayload?.filter(
      payload => !payload.hide && !isNil(payload.value) && !hiddenTraces.includes(payload.dataKey)
    )

    const IndicatorLine = (
      <>
        <Tooltip
          cursor={{
            stroke: 'transparent',
            fill: 'transparent',
          }}
          content={() => null}
          active={true}
          isAnimationActive={false}
        />
        {type === 'line' ? (
          <>
            <ReferenceLine xAxisId={0} yAxisId={'left'} x={hoverPayload?.activeLabel} stroke={theme.palette.grey.grey02} strokeDasharray="5 5" />
            <g>
              {filteredPayload?.map((payload, index) => {
                if (!isTouchDevice || !hoverPayload || presetChartToDisplay === 'consumption') return null
                const cartesianGridHeight = getCartesianGridHeight()
                const leftYAxisMaxValue = getLeftYAxisMaxValue()
                const { cxValue, cyValue } = getCircleCoordinates(payload, hoverPayload, cartesianGridHeight, leftYAxisMaxValue)

                return <circle key={index} cx={cxValue} cy={cyValue} r="4" stroke={theme.palette.common.white} strokeWidth="2" fill={payload.color} />
              })}
            </g>
          </>
        ) : (
          <ReferenceArea
            yAxisId={'right'}
            x1={hoverPayload?.activeLabel}
            x2={hoverPayload?.activeLabel}
            stroke={theme.palette.grey.grey02}
            strokeOpacity={0.7}
            strokeDasharray="5 5"
            pointerEvents="none"
          />
        )}
      </>
    )

    switch (type) {
      case 'pie':
        if (isLoading) {
          return (
            <PieChartSkeleton
              height={skeletonErrorHeight}
              hideLoadingSpinner
              loading
              progressDetails={<ProgressDetails loadingState={loadingState} />}
            />
          )
        }
        return (
          <PieChart
            data={pieChartData ?? []}
            cells={pieChartCells ?? []}
            savingsSum={savingsSum}
            traceNames={traceNames}
            height={height}
            width={height}
            headerHeight={headerHeight}
            legendHeight={legendHeight}
            title={title}
            labelTraces={labelTraces}
            labelNameKey={labelNameKey}
            isCostSavingsPieChart={isCostSavingsPieChart}
            isWeightedAveragePriceOn={isWeightedAveragePriceOn}
            weightedPrice={weightedPrice}
            labelAction={pieChartLabelAction}
          />
        )
      case 'bar':
        if (isLoading) {
          return <BarChartSkeleton hideLoadingSpinner loading progressDetails={<ProgressDetails loadingState={loadingState} />} />
        }

        return (
          <BarChart
            unit={unit}
            secondUnit={secondUnit}
            xAxisFormat={xAxisFormat}
            xAxisInterval={xAxisInterval}
            rechartData={rechartData!}
            customTraces={customTraces}
            showForecast={showForecast}
            impossibleTraces={impossibleTraces}
            hiddenTraces={hiddenTraces}
            dstSwitchTs={dstSwitchTs}
            xAxisTicks={xAxisTicks}
            onElementClick={onElementClick}
            height={height}
            legendHeight={legendHeight}
            headerHeight={headerHeight}
            minPrefix={minPrefix}
            view={view}
            hasBrush={hasBrush}
            chartEventHandlers={chartEventHandlers}
            zoomStartIndex={zoomStartIndex}
            zoomEndIndex={zoomEndIndex}
            handleDoubleClick={handleDoubleClick}
            calculatedMinPrefix={yAxisComputedMinPrefix}
          >
            {IndicatorLine}
          </BarChart>
        )
      case 'comparison':
        if (isLoading) {
          return <BarChartSkeleton hideLoadingSpinner loading progressDetails={<ProgressDetails loadingState={loadingState} />} />
        }
        return (
          <ComparisonChart
            unit={unit}
            secondUnit={secondUnit}
            xAxisFormat={xAxisFormat}
            rechartData={rechartData!}
            customTraces={customTraces}
            showForecast={showForecast}
            impossibleTraces={impossibleTraces}
            hiddenTraces={hiddenTraces}
            dstSwitchTs={dstSwitchTs}
            xAxisTicks={xAxisTicks}
            onElementClick={onElementClick}
            height={height}
            legendHeight={legendHeight}
            headerHeight={headerHeight}
            minPrefix={minPrefix}
            traceSuffixes={traceSuffixes}
            chartEventHandlers={chartEventHandlers}
            zoomStartIndex={zoomStartIndex}
            zoomEndIndex={zoomEndIndex}
            calculatedMinPrefix={yAxisComputedMinPrefix}
          >
            {IndicatorLine}
          </ComparisonChart>
        )
      case 'composed':
        if (loadingState?.state === FetchingState.IsFetching) {
          return <BarChartSkeleton hideLoadingSpinner loading progressDetails={<ProgressDetails loadingState={loadingState} />} />
        }
        return (
          <ComposedChart
            unit={unit}
            secondUnit={secondUnit}
            xAxisFormat={xAxisFormat}
            xAxisInterval={xAxisInterval}
            rechartData={rechartData!}
            customTraces={customTraces}
            showForecast={showForecast}
            impossibleTraces={impossibleTraces}
            hiddenTraces={hiddenTraces}
            dstSwitchTs={dstSwitchTs}
            xAxisTicks={xAxisTicks}
            onElementClick={onElementClick}
            height={height}
            legendHeight={legendHeight}
            headerHeight={headerHeight}
            minPrefix={minPrefix}
            view={view}
            chartEventHandlers={chartEventHandlers}
            zoomStartIndex={zoomStartIndex}
            zoomEndIndex={zoomEndIndex}
            handleDoubleClick={handleDoubleClick}
            calculatedMinPrefix={yAxisComputedMinPrefix}
          >
            {IndicatorLine}
          </ComposedChart>
        )
      case 'line':
      default:
        if (isLoading) {
          return <LineChartSkeleton hideLoadingSpinner />
        }
        return (
          <LineChart
            unit={unit}
            secondUnit={secondUnit}
            xAxisFormat={xAxisFormat}
            xAxisInterval={xAxisInterval}
            rechartData={rechartData!}
            customTraces={customTraces}
            showForecast={showForecast}
            impossibleTraces={impossibleTraces}
            hiddenTraces={hiddenTraces}
            xAxisTicks={xAxisTicks}
            dstSwitchTs={dstSwitchTs}
            onElementClick={onElementClick}
            height={height}
            legendHeight={legendHeight}
            headerHeight={headerHeight}
            minPrefix={minPrefix}
            view={view}
            chartEventHandlers={chartEventHandlers}
            zoomStartIndex={zoomStartIndex}
            zoomEndIndex={zoomEndIndex}
            handleDoubleClick={handleDoubleClick}
            isWeatherBreakthroughDotVisible={isWeatherBreakthroughDotVisible}
            calculatedMinPrefix={yAxisComputedMinPrefix}
          >
            {IndicatorLine}
          </LineChart>
        )
    }
  }, [
    yAxisComputedMinPrefix,
    weightedPrice,
    hoverPayload,
    hiddenTraces,
    impossibleTraces,
    rechartData,
    showForecast,
    type,
    unit,
    xAxisFormat,
    view,
    skeletonErrorHeight,
    height,
    legendHeight,
    minPrefix,
    xAxisInterval,
    xAxisTicks,
    chartEventHandlers,
    classes.pieChartErrorContainer,
    customTraces,
    dstSwitchTs,
    error,
    handleDoubleClick,
    hasBrush,
    isLoading,
    labelNameKey,
    labelTraces,
    loadingState,
    onElementClick,
    secondUnit,
    successfullyFetchedNoData,
    t,
    theme,
    title,
    traceNames,
    traceSuffixes,
    zoomEndIndex,
    zoomStartIndex,
    headerHeight,
    isCostSavingsPieChart,
    pieChartCells,
    pieChartData,
    savingsSum,
    isWeightedAveragePriceOn,
    pieChartLabelAction,
    isTouchDevice,
    presetChartToDisplay,
    isWeatherBreakthroughDotVisible,
  ])

  const summary = useMemo(() => {
    if (type === 'pie') return <PieChartLegend payload={pieChartLegendPayload} />
    const isWeatherBreakthroughReached = rechartData?.hasWeatherBreakthrough ?? false

    return (
      <Summary
        traceNames={traceNames}
        sums={rechartData?.sums ?? {}}
        showForecast={showForecast}
        impossibleTraces={impossibleTraces}
        hiddenTraces={hiddenTraces}
        disabledTraces={hiddenInChart}
        defaultUnit={summaryUnit}
        onTraceToggle={handleTraceToggle}
        colors={customTraces?.map(trace => ({ color: trace.color, key: trace.alternateKey ?? trace.key }))}
        from={from}
        hasSuffixes={type === 'comparison'}
        hoverPayload={hoverPayload}
        view={view}
        setIsWeatherBreakthroughDotVisible={setIsWeatherBreakthroughDotVisible}
        isWeatherBreakthroughDotVisible={isWeatherBreakthroughDotVisible}
        isWeatherBreakthroughReached={isWeatherBreakthroughReached}
      />
    )
  }, [
    type,
    pieChartLegendPayload,
    traceNames,
    rechartData?.sums,
    rechartData?.hasWeatherBreakthrough,
    showForecast,
    impossibleTraces,
    hiddenTraces,
    hiddenInChart,
    summaryUnit,
    handleTraceToggle,
    customTraces,
    from,
    hoverPayload,
    view,
    isWeatherBreakthroughDotVisible,
  ])

  const showLegend = (!hideLegend || !downSm) && !isLoading

  return (
    <Box
      data-qa={dataQa}
      className={cx({
        [classes.pieChartContainer]: type === 'pie',
        [classes.chartContainer]: type !== 'pie',
        [classes.pieChartErrorContainer]: type === 'pie' && (!!error || successfullyFetchedNoData),
      })}
    >
      {chart}
      <Box
        ref={ref}
        className={cx({ [classes.pieLegend]: type === 'pie' })}
        sx={{ position: 'relative', width: type === 'pie' ? 'fit-content' : '100%', justifySelf: 'left', paddingInline: 2 }}
      >
        {showLegend && type !== 'pie' && (
          <Typography
            variant="subtitle1"
            sx={theme => ({
              pt: 2,
              userSelect: 'none',

              [theme.breakpoints.up('sm')]: {
                pt: 1,
                userSelect: 'auto',
              },
            })}
          >
            {dateTimeToDisplayAsLegend}
          </Typography>
        )}
        {showLegend && <>{summary}</>}
      </Box>
    </Box>
  )
}

export default Chart
